import { createSelector } from '@reduxjs/toolkit';
import type { ToolConfig, ToolConfigDb } from '@timelyapp/integration-cloud-api-spec';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import type { IState } from 'redux/state';
import { keyBy } from 'utils/typesafe';
import { ticApi } from '../api-slice/tic.apiSlice';
import { getIsViewingDefaults } from '../tic.config_defaults';

const readTicApiSlice = (state: IState) => state[ticApi.reducerPath];

export const getToolConfigsRTKQueryBase = createSelector([readTicApiSlice], localState => {
  return ticApi.endpoints.getToolConfigs.select(undefined)({ [ticApi.reducerPath]: localState });
});

export const getDefaultToolConfigsRTKQueryBase = createSelector([readTicApiSlice], localState => {
  return ticApi.endpoints.getDefaultToolConfigs.select(undefined)({
    [ticApi.reducerPath]: localState,
  });
});

export const getToolConfigs = createSelector(
  [getToolConfigsRTKQueryBase, getDefaultToolConfigsRTKQueryBase, getIsViewingDefaults],
  (toolConfigsResult, defaultToolConfigsResult, isViewingDefaults): ToolConfig[] => {
    if (isViewingDefaults) {
      return (defaultToolConfigsResult?.data?.configs || EMPTY_ARRAY) as ToolConfig[];
    }
    return (toolConfigsResult?.data?.configs || EMPTY_ARRAY) as ToolConfig[];
  }
);

/**
 * Returns a map of ToolConfigs keyed by `tool_id` or `tool_raw`,
 * similar to how `GET /memories` returns them (or used to, depending on how far the cleanup goes)
 */
export const getToolConfigsMap = createSelector(
  [getToolConfigs],
  (toolConfigs): Record<string, ToolConfig> => {
    if (!toolConfigs || toolConfigs.length === 0) {
      return EMPTY_OBJECT;
    }

    return keyBy(toolConfigs, o => {
      return o?.tool_id || o?.tool_raw || 'unknown';
    });
  }
);

/**
 * Returns a specific tool config by toolId
 * This is a memoized selector that will only recompute when the tool config for the specified toolId changes
 */
export const getToolConfigById = createSelector(
  [getToolConfigsMap, (_, toolId: string | undefined) => toolId],
  (toolConfigsMap, toolId): ToolConfig | ToolConfigDb | undefined => {
    if (!toolId) return undefined;
    return toolConfigsMap[toolId];
  }
);
